import './index.scss';
import React, { useEffect, useRef, useState } from 'react';
import {
    IMultiCapabilityTemplatePageInfo,
    ActType,
    ITemplateComponentProps,
    PanelType,
    ITemplateListInfo,
    ISubmitData,
    TemplateQuestionInfoMap,
} from '../interface/multiCapabilityTemplate';
import { defaultPageInfo } from '../dataFlow/multiCapabilityTemplate';
import { Iconfont, Modal } from '@umu/umu-design';
import { panelTypeMapInfo } from './page';
import {
    getTemplateAllListInfo,
    getTemplateQuestionList,
    getTemplateTypeCheck,
    onUpdateTemplateList,
} from './dataFlow';
import merge from 'lodash/merge';
import { ModalFuncProps } from '@umu/design-base';
interface IProps {
    preCls?: string;
    panelType?: PanelType;
    info?: ISubmitData;
    onClose?: VoidFunction;
    onSubmit?: (data: ISubmitData) => void;
}
const { confirm } = Modal;
const MultiCapabilityTemplate = (props: IProps) => {
    const { onClose, onSubmit } = props;
    const refTemplateMapInfo = useRef<TemplateQuestionInfoMap>({});
    const [pageDataInfo, setPageInfo] = useState<IMultiCapabilityTemplatePageInfo>(defaultPageInfo);
    const { preCls = 'multi-capability-template-info' } = props;
    const { panelType, title } = pageDataInfo;
    const panelInfo = panelTypeMapInfo[panelType];
    const {
        pageComponent: { HeardContent, BodyContent, FooterContent },
    } = panelInfo;
    const onPanelClose = () => {
        onClose?.();
    };
    const onPanelSubmit = async () => {
        const { listSelectInfo } = pageDataInfo.dataSource;
        if (!listSelectInfo) {
            return;
        }
        if (listSelectInfo?.info?.list) {
            onSubmit?.({
                parent_type: listSelectInfo.templateType,
                templateInfo: listSelectInfo.info,
            });
        } else {
            const resultData = await getTemplateQuestionList(listSelectInfo, { mapInfo: refTemplateMapInfo });
            onSubmit?.(resultData);
        }
    };

    const onCommunication = async (type: ActType, data: any) => {
        const { info, isRequireTemplateList = false, isReloadList = false } = data;
        switch (type) {
            case ActType.UPDATE_PANEL_TYPE:
                const templateInfo = info?.templateInfo || pageDataInfo.templateInfo;
                if (isRequireTemplateList) {
                    const dataSource = await getTemplateQuestionList(
                        {
                            templateType: pageDataInfo.templateType,
                            info: templateInfo,
                        },
                        { mapInfo: refTemplateMapInfo },
                    );
                    info['templateInfo'] = dataSource.templateInfo;
                }
                const newPageDataInfo = {
                    ...pageDataInfo,
                    title: panelTypeMapInfo[info.panelType as PanelType].title,
                    ...info,
                };

                if (isReloadList) {
                    onUpdateTemplateList(
                        {
                            parent_type: pageDataInfo.templateType,
                            page: 1,
                            size: 10,
                        },
                        (res) => {
                            newPageDataInfo.dataSource.listData[pageDataInfo.templateType] = res;
                            setPageInfo({ ...newPageDataInfo });
                        },
                    );
                } else {
                    setPageInfo(newPageDataInfo);
                }
                break;
            case ActType.UPDATE_TEMPLATE_TYPE:
                setPageInfo({ ...pageDataInfo, templateType: info.templateType });
                break;
            case ActType.UPDATE_TEMPLATE_LIST:
                onUpdateTemplateList(
                    {
                        parent_type: pageDataInfo.templateType,
                        page: info.page,
                        size: 10,
                    },
                    (res) => {
                        pageDataInfo.dataSource.listData[pageDataInfo.templateType] = res;
                        setPageInfo({ ...pageDataInfo });
                    },
                );
                break;
            case ActType.UPDATE_TEMPLATE_SELECT:
                setPageInfo(
                    merge({}, pageDataInfo, {
                        dataSource: {
                            listSelectInfo: info,
                        },
                    }),
                );
                break;
            case ActType.PANEL_CANCEL:
                onPanelClose();
                break;
            case ActType.PANEL_SUBMIT:
                if (data?.templateInfo) {
                    onSubmit?.(data.templateInfo);
                } else {
                    onPanelSubmit();
                }

                break;
        }
        return true;
    };
    const childProps: ITemplateComponentProps = {
        ...pageDataInfo,
        onCommunication,
    };
    const onInit = async () => {
        const defaultPanelType = props?.panelType || PanelType.LIST;
        switch (defaultPanelType) {
            case PanelType.LIST:
                // 初始化
                const [list, server] = getTemplateAllListInfo();
                Promise.all(server).then((res) => {
                    const listData: Record<string, ITemplateListInfo> = {};
                    list.forEach((item, index) => {
                        listData[item] = res[index];
                    });

                    const newTemplateType = getTemplateTypeCheck(listData, pageDataInfo.templateType);
                    setPageInfo(
                        merge({}, pageDataInfo, {
                            templateType: newTemplateType,
                            dataSource: {
                                listData,
                            },
                            isLoading: false,
                        }),
                    );
                });
                break;
            case PanelType.PREVIEW:
            case PanelType.EDIT:
                const { info } = props;
                const configInfo = {
                    title: panelTypeMapInfo[defaultPanelType].title,
                    panelType: defaultPanelType,
                    isLoading: false,
                    editorConfig: {
                        isShowPreview: false, // 是否展示预览按钮
                        isShowPreStep: false, // 是否展示上一步
                        isShowCancel: false, // 是否展示取消按钮
                    },
                };
                if (info?.templateInfo) {
                    const resultData = info?.templateInfo?.list
                        ? info
                        : await getTemplateQuestionList(
                              {
                                  templateType: info.parent_type,
                                  info: info.templateInfo,
                              },
                              { mapInfo: refTemplateMapInfo },
                          );
                    setPageInfo(
                        merge({}, pageDataInfo, {
                            ...configInfo,
                            templateInfo: resultData.templateInfo,
                            templateType: resultData.parent_type,
                        }),
                    );
                }
                break;
            default:
                break;
        }
    };
    useEffect(() => {
        onInit();
    }, []);
    const onPanelBack = () => {
        const { previewPanelType = [] } = pageDataInfo;
        if (previewPanelType?.length < 1) {
            return;
        }
        const newPanelType = previewPanelType.pop();
        onCommunication(ActType.UPDATE_PANEL_TYPE, {
            info: {
                panelType: newPanelType,
                isShowBack: previewPanelType.length > 0,
                previewPanelType: previewPanelType,
            },
        });
    };
    return (
        <div className={preCls}>
            <div className={`capability-flex-column ${preCls}-heard`}>
                <div className={`capability-flex-col capability-space-between`}>
                    <div className="capability-flex-col">
                        {pageDataInfo.isShowBack && (
                            <Iconfont type="ArrowBack" className="icon-info" onClick={onPanelBack} />
                        )}
                        <span className="large-label">{title}</span>
                    </div>
                    <Iconfont type="Close" className="icon-info" onClick={onPanelClose} />
                </div>
                {HeardContent && <HeardContent {...childProps} />}
            </div>
            <div className={`${preCls}-content`}>
                <BodyContent {...childProps} />
            </div>
            {FooterContent && (
                <div className={`${preCls}-footer`}>
                    <FooterContent {...childProps} />
                </div>
            )}
        </div>
    );
};
const onDialogMultiCapabilityTemplate = (info?: IProps, dialogInfo?: ModalFuncProps) => {
    const { title, ...rest } = dialogInfo || {};
    const dialog = confirm({
        title,
        centered: true,
        closable: false,
        width: '8.6rem',
        zIndex: 1000,
        className: 'multi-capability-template-dialog',
        footer: null,
        ...rest,
        content: (
            <MultiCapabilityTemplate
                {...info}
                onClose={() => {
                    info?.onClose ? info?.onClose() : dialog.destroy();
                }}
                onSubmit={(data) => {
                    info?.onSubmit?.(data);
                    dialog.destroy();
                }}
            />
        ),
    } as any);
    return dialog;
};
export { MultiCapabilityTemplate, onDialogMultiCapabilityTemplate };
